import {
  EProjectType,
  IComponentDefinition,
  IDisplayItem,
  IDisplayItemComponent,
  IScreenDesign,
  isAppSupportTypeOfferedOnScreen,
  SelectionAction,
  SpecialDIIds
} from "@kuika/kuika-cl-model";
import { IUndoHistoryMarker } from "../../../../contexts/screen-design-context/provider-partitions/screen-provider";
import { IScreenDesignContextState } from "../../../../contexts/screen-design-context/screen-design-context";
import { IDisplayItemClipboard } from "../../../../models/interface/display-item-clipboard";
import { KContext } from "../../../../shared/hoc/with-context";
import { ScreenDesignHelper } from "../../../../shared/utility/screen-design-helper";
import { getMoveDownReorder, getMoveUpReorder, ISiblingReorder } from "../../../../shared/utility/sibling-reorder";
import { toastService } from "../../../ui-kit/kuika-toast/toast-service";
import { SelectionMenuKey } from "./selection-menu";

/** The slice of the screen-design context the selection overlay reads and drives. */
export type ISelectionOverlaySd = Pick<
  IScreenDesignContextState,
  | "dashboardState"
  | "isReportDesignerLandingVisible"
  | "selectedKuikaIds"
  | "screenDesignDictionary"
  | "sdName"
  | "project"
  | "getComponentDefinition"
  | "getScreenDesign"
  | "setSelectedDisplayItem"
  | "highlightHoveredItem"
  | "setActiveRightPanelByClick"
  | "undo"
  | "getUndoHistoryMarker"
  | "mergeUndoHistorySince"
  | "getIsWebScreen"
  | "removeItemMessage"
  | "copyItemMessage"
  | "pasteItemMessage"
  | "pasteItemStyleMessage"
  | "cloneItemMessage"
  | "detachForm"
  | "handleOpenSaveBlockAsCustomBlockModal"
  | "reOrderItems"
  // Quick props (quick-props/): property resolution, summaries and the child-adding commands.
  | "getPropertyDefinition"
  | "selectedDeviceType"
  | "actionDefinitions"
  | "getDefinedActions"
  | "addChildItemByComponentName"
>;

export const UNDO_TOAST_ACTION_TEST_ID: string = "selectionUndoToastAction";

const ROOT_KUIKA_ID: string = SpecialDIIds.rootDI.toString();

/** The parent to select for "Select parent" / Esc; null for the screen root (or an unknown id). */
export function getParentKuikaId(sd: ISelectionOverlaySd, kuikaId: string): string | null {
  if (kuikaId === ROOT_KUIKA_ID) {
    return null;
  }
  const parentId: number | undefined = sd.screenDesignDictionary[parseInt(kuikaId, 10)]?.parentId;
  if (parentId === undefined || parentId === null) {
    return null;
  }
  return parentId.toString();
}

const containsDisplayItem = (items: IDisplayItem[] | undefined, id: number): boolean => {
  if (!items) {
    return false;
  }
  return items.some((item: IDisplayItem): boolean => item.id === id || containsDisplayItem(item.children, id));
};

/**
 * After a delete the selection moves to the deleted component's parent — the container the user was
 * working in — not to the screen. `parentId` is read BEFORE the removal (the dictionary still knows it
 * then); a removal can take an emptied fixed-children parent with it, so the nearest ancestor still in
 * the tree wins, and the screen root is the floor.
 */
function selectSurvivingAncestor(sd: ISelectionOverlaySd, parentId: string | null, screenDesign: IScreenDesign): void {
  let candidate: string | null = parentId;
  while (candidate !== null && !containsDisplayItem(screenDesign?.displayItems, parseInt(candidate, 10))) {
    candidate = getParentKuikaId(sd, candidate);
  }
  sd.setSelectedDisplayItem(candidate ?? ROOT_KUIKA_ID);
}

/** The deleted components' shared parent, or the screen root when they come from different parents. */
function commonParentKuikaId(sd: ISelectionOverlaySd, kuikaIds: readonly string[]): string {
  const parents: Set<string> = new Set<string>(
    kuikaIds.map((kuikaId: string): string => getParentKuikaId(sd, kuikaId) ?? ROOT_KUIKA_ID)
  );
  const [only] = Array.from(parents);
  return parents.size === 1 && only !== undefined ? only : ROOT_KUIKA_ID;
}

/**
 * The Undo a delete toast offers: exactly the history entries the delete pushed. A removal can push more
 * than one (an emptied fixed-children parent goes with it, an empty absolute Panel is pruned), so the
 * count is the growth of the uncapped append counter across the delete, not the number of ids (and not
 * `past.length`, which stops growing once the capped stack is full). When nothing was recorded, there is
 * nothing the toast could safely undo.
 */
interface IDeleteUndo {
  steps: number;
  /** The history right after the delete; see `hasHistoryMovedOn`. */
  after: IUndoHistoryMarker;
}

function measureDeleteUndo(sd: ISelectionOverlaySd, before: IUndoHistoryMarker): IDeleteUndo | null {
  const after: IUndoHistoryMarker = sd.getUndoHistoryMarker();
  const steps: number = after.appendCount - before.appendCount;
  return steps > 0 ? { steps, after } : null;
}

/**
 * Whether the user's own history moved since the delete: a local edit (appendCount) or an undo / redo
 * (undoDepth). Not the global `revision`: that also moves for changes that push no local entry — a
 * collaborative resync replaces the top entry in place — and refusing the Undo for those left the
 * delete toast's Undo doing nothing.
 */
const hasHistoryMovedOn = (now: IUndoHistoryMarker, after: IUndoHistoryMarker): boolean =>
  now.appendCount !== after.appendCount || now.undoDepth !== after.undoDepth;

export const UNDO_MOVED_ON_MESSAGE: string = "Can't undo the delete: the screen has changed since.";

/**
 * Undoes the delete's own history entries — and nothing else. Once any other edit, undo or redo has
 * moved the history since the delete, stepping back by the same count would revert unrelated work, so
 * the click does nothing but say so.
 */
function runDeleteUndo(sd: ISelectionOverlaySd, undo: IDeleteUndo): void {
  if (hasHistoryMovedOn(sd.getUndoHistoryMarker(), undo.after)) {
    toastService.info(UNDO_MOVED_ON_MESSAGE);
    return;
  }
  for (let i: number = 0; i < undo.steps; i += 1) {
    sd.undo();
  }
}

function offerDeleteUndo(sd: ISelectionOverlaySd, message: string, undo: IDeleteUndo | null): void {
  if (undo === null) {
    return;
  }
  toastService.neutralWithAction(message, {
    label: "Undo",
    onClick: (): void => runDeleteUndo(sd, undo),
    testId: UNDO_TOAST_ACTION_TEST_ID
  });
}

/**
 * Deletes the component, selects its parent and offers Undo in a neutral toast. The removal can be
 * refused (a child with disabled interaction, a layout region) — the helper warns on its own then — so
 * nothing else happens unless the component is actually gone from the screen design.
 */
function deleteWithUndo(sd: ISelectionOverlaySd, kuikaId: string): void {
  const name: string = ScreenDesignHelper.getFriendlyName(kuikaId, sd.screenDesignDictionary);
  const parentId: string | null = getParentKuikaId(sd, kuikaId);
  const historyBefore: IUndoHistoryMarker = sd.getUndoHistoryMarker();
  sd.removeItemMessage({ kuikaid: kuikaId });
  const screenDesign: IScreenDesign = sd.getScreenDesign();
  if (containsDisplayItem(screenDesign?.displayItems, parseInt(kuikaId, 10))) {
    return;
  }
  selectSurvivingAncestor(sd, parentId, screenDesign);
  offerDeleteUndo(sd, `${name} deleted`, measureDeleteUndo(sd, historyBefore));
}

function runReorder(sd: ISelectionOverlaySd, reorder: ISiblingReorder | null): void {
  if (reorder) {
    sd.reOrderItems(reorder.sourceKuikaId, reorder.targetKuikaId, reorder.nextSelectedKuikaId);
  }
}

/**
 * Runs one ⋯ menu row for a single selected component. These are the designer functions the agent's old
 * toolbar reached through frame messages; the overlay lives in the designer, so it calls them directly.
 */
export function runSelectionMenuAction(sd: ISelectionOverlaySd, key: SelectionMenuKey, kuikaId: string): void {
  switch (key) {
    case "copy":
      sd.copyItemMessage({ kuikaid: kuikaId });
      return;
    case "paste":
      sd.pasteItemMessage({ kuikaid: kuikaId });
      return;
    case "pasteStyle":
      sd.pasteItemStyleMessage({ kuikaid: kuikaId });
      return;
    case "duplicate":
      sd.cloneItemMessage({ kuikaid: kuikaId });
      return;
    case "detach":
      sd.detachForm(kuikaId);
      return;
    case "selectParent": {
      const parentId: string | null = getParentKuikaId(sd, kuikaId);
      if (parentId !== null) {
        sd.setSelectedDisplayItem(parentId);
      }
      return;
    }
    case "moveUp":
      runReorder(sd, getMoveUpReorder(sd.screenDesignDictionary, parseInt(kuikaId, 10)));
      return;
    case "moveDown":
      runReorder(sd, getMoveDownReorder(sd.screenDesignDictionary, parseInt(kuikaId, 10)));
      return;
    case "saveAsBlock":
      sd.handleOpenSaveBlockAsCustomBlockModal(kuikaId);
      return;
    case "delete":
      deleteWithUndo(sd, kuikaId);
      return;
    default: {
      const unhandled: never = key;
      throw new Error(`Unhandled selection menu action: ${String(unhandled)}`);
    }
  }
}

/** The multi-select bar's own actions — the same three the agent's keyboard shortcuts run for a
 * multi-selection (`copyMultiSelectedItems` / `cloneMultiSelectedItems` / `removeMultiSelectedItems`,
 * react-helper.tsx), so the bar and the keys agree. */
export type MultiSelectionActionKey = "copy" | "duplicate" | "delete";

/**
 * Drops any id in `kuikaIds` whose ancestor is also in `kuikaIds` — a multi-selection spanning a
 * container and one of its own children would otherwise clone/delete that child twice (once
 * directly, once as part of the container).
 */
export function filterTopMostIds(sd: ISelectionOverlaySd, kuikaIds: readonly string[]): string[] {
  const idSet: Set<string> = new Set(kuikaIds);
  return kuikaIds.filter((kuikaId: string): boolean => {
    let ancestorId: string | null = getParentKuikaId(sd, kuikaId);
    while (ancestorId !== null) {
      if (idSet.has(ancestorId)) {
        return false;
      }
      ancestorId = getParentKuikaId(sd, ancestorId);
    }
    return true;
  });
}

/**
 * Deletes every top-most selected id, selects their common parent (the screen when they come from
 * different parents) and offers one Undo toast for all of them. Each `removeItemMessage` commits
 * synchronously (`removeItem` → `setScreenDesign(…, DragDropDi)` → one `CHANGE_SD` dispatch; only
 * `DragDropBlock` defers), so `sd.getScreenDesign()` and the undo stack already reflect every removal when
 * the loop returns — and the removals' entries are folded into one (`mergeUndoHistorySince`). A removal can be silently refused (disabled interaction, a layout region) — only ids
 * verifiably gone from the tree count toward the message, and the Undo steps back by exactly the stack's
 * growth (see `measureDeleteUndo`).
 */
function deleteMultiWithUndo(sd: ISelectionOverlaySd, kuikaIds: readonly string[]): void {
  const parentId: string = commonParentKuikaId(sd, kuikaIds);
  const historyBefore: IUndoHistoryMarker = sd.getUndoHistoryMarker();
  kuikaIds.forEach((kuikaId: string): void => sd.removeItemMessage({ kuikaid: kuikaId }));
  // One delete, one undo step: ⌘Z (and the toast's Undo) brings every component back at once.
  sd.mergeUndoHistorySince(historyBefore);
  const screenDesign: IScreenDesign = sd.getScreenDesign();
  const removedCount: number = kuikaIds.filter(
    (kuikaId: string): boolean => !containsDisplayItem(screenDesign?.displayItems, parseInt(kuikaId, 10))
  ).length;
  if (removedCount === 0) {
    return;
  }
  selectSurvivingAncestor(sd, parentId, screenDesign);
  const message: string = `${removedCount} component${removedCount === 1 ? "" : "s"} deleted`;
  offerDeleteUndo(sd, message, measureDeleteUndo(sd, historyBefore));
}

/** Runs one multi-select bar button for the current multi-selection. */
export function runMultiSelectionAction(
  sd: ISelectionOverlaySd,
  key: MultiSelectionActionKey,
  kuikaIds: readonly string[]
): void {
  const topMostIds: string[] = filterTopMostIds(sd, kuikaIds);
  if (topMostIds.length === 0) {
    return;
  }
  switch (key) {
    case "copy":
      // The clipboard holds a single display item (IDisplayItemClipboard) — mirrors the agent's
      // copyMultiSelectedItems, which copies only the first selected id.
      sd.copyItemMessage({ kuikaid: topMostIds[0] });
      return;
    case "duplicate": {
      const historyBefore: IUndoHistoryMarker = sd.getUndoHistoryMarker();
      topMostIds.forEach((kuikaId: string): void => sd.cloneItemMessage({ kuikaid: kuikaId }));
      sd.mergeUndoHistorySince(historyBefore);
      return;
    }
    case "delete":
      deleteMultiWithUndo(sd, topMostIds);
      return;
    default: {
      const unhandled: never = key;
      throw new Error(`Unhandled multi-selection action: ${String(unhandled)}`);
    }
  }
}

/** Whether the ⋯ menu's Paste / Paste style rows have anything to act on. */
export interface IClipboardPasteGates {
  canPaste: boolean;
  canPasteStyle: boolean;
}

/** Components that only ever live inside a table's column structure and cannot be pasted anywhere. */
const UNPASTEABLE_COMPONENT_NAMES: readonly string[] = ["EditableTableColumn", "TableDefaultColumn"];

/** Everything `pasteItem` (drag-drop-helper.ts) checks about the paste target before it pastes. */
export interface IPasteIntoInput {
  /** The copied component's definition (name + platform flag); undefined when it cannot be resolved. */
  clipboardDefinition: Pick<IComponentDefinition, "name" | "appSupportType"> | undefined;
  projectType: EProjectType | undefined;
  isWebScreen: boolean;
  reactNativeOffered: boolean;
  /** The target sits inside an editable table column (`CommonHelperFunctions.getIsEditableTableColumnChild`). */
  isTargetEditableTableColumnChild: boolean;
}

/**
 * Whether `pasteItem` would accept the clipboard onto this target — the same platform predicate as the
 * palette (`isAppSupportTypeOfferedOnScreen`) and the same table-column refusals. `pasteItem` warns or
 * silently returns in exactly these cases, so offering Paste for them would be a dead menu row.
 */
export function canPasteInto(input: IPasteIntoInput): boolean {
  const isPlatformOffered: boolean = isAppSupportTypeOfferedOnScreen({
    appSupportType: input.clipboardDefinition?.appSupportType,
    projectType: input.projectType,
    isWebScreen: input.isWebScreen,
    reactNativeOffered: input.reactNativeOffered
  });
  if (!isPlatformOffered || input.isTargetEditableTableColumnChild) {
    return false;
  }
  const name: string | undefined = input.clipboardDefinition?.name;
  return name === undefined || !UNPASTEABLE_COMPONENT_NAMES.includes(name);
}

/** The paste target, for the gates that depend on where the clipboard would land. */
export interface IPasteTargetContext extends Omit<IPasteIntoInput, "clipboardDefinition"> {
  resolveDefinition: (item: IDisplayItemComponent) => IComponentDefinition | undefined;
}

/**
 * The agent's `availableActions` allows "paste"/"pasteStyle" whenever the structural gates
 * (root, locked, form-child, ...) allow it — it does not know about the designer's own
 * clipboard, which lives outside React state (`KContext`/`window.kuika.clipboard`, written by
 * `drag-drop-helper.ts`'s `copyItem`). The designer is responsible for hiding Paste/Paste style on
 * top of that when there is nothing pasteable. `pasteItem` also refuses a component the target screen's
 * platform does not offer and table-column pieces (`canPasteInto`, checked when `pasteTarget` is given),
 * and `pasteItemStyle` refuses across component types, so `canPasteStyle` mirrors that exact check when
 * `targetComponentName` is given.
 */
export function computeClipboardPasteGates(
  targetComponentName?: string,
  pasteTarget?: IPasteTargetContext
): IClipboardPasteGates {
  const rawClipboard: IDisplayItemClipboard | string | undefined = new KContext().getClipboardValue("displayItem") as
    | IDisplayItemClipboard
    | string
    | undefined;
  if (rawClipboard === undefined || rawClipboard === "") {
    return { canPaste: false, canPasteStyle: false };
  }
  // pasteItem/pasteItemStyle (drag-drop-helper.ts) re-cast the same way: TS can't narrow away a
  // single string literal ("") from the general `string` half of the union above.
  const clipboard: IDisplayItemClipboard = rawClipboard as IDisplayItemClipboard;
  const copiedItem: IDisplayItem | undefined = clipboard.displayItem;
  const isTargetAccepting: boolean =
    pasteTarget === undefined ||
    (copiedItem != null &&
      canPasteInto({
        ...pasteTarget,
        clipboardDefinition: copiedItem.item ? pasteTarget.resolveDefinition(copiedItem.item) : undefined
      }));
  const canPaste: boolean = copiedItem != null && isTargetAccepting;
  const canPasteStyle: boolean =
    copiedItem?.styleObj != null &&
    (targetComponentName === undefined || copiedItem.item?.name === targetComponentName);
  return { canPaste, canPasteStyle };
}

/** Hides "paste"/"pasteStyle" from an `availableActions` list when the clipboard gates refuse them. */
export function filterActionsByClipboard(
  actions: readonly SelectionAction[],
  gates: IClipboardPasteGates
): SelectionAction[] {
  return actions.filter((action: SelectionAction): boolean => {
    if (action === "paste") {
      return gates.canPaste;
    }
    if (action === "pasteStyle") {
      return gates.canPasteStyle;
    }
    return true;
  });
}
